import React, { ChangeEvent, Component } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { MenuItem, Checkbox, Button, FormHelperText } from "@material-ui/core";
import DefaultLayout from "layouts/Default";
import ToggleButton from "components/forms/ToggleButton";
import { FormStatus } from "components/forms/Form";
import { ValidationGroup, Validators } from "util/validation";
import NumberField from "components/forms/NumberField";
import SelectField from "components/forms/SelectField";
import Form from "components/forms/Form";
import NextRouteURLs from 'NextRouteURLs';
import { formatCurrency } from "util/currency";
import { DAY_NAMES } from "util/time";
import config from "config";
import "./index.scss";

enum LeaseTerm {
  LongTerm,
  ShortTerm,
}

interface TenantCalculatorState {
  leaseTerm: LeaseTerm | null;
  formStatus: FormStatus;
  savings: number | null;
}

enum TradingTime {
  Morning,
  Daytime,
  Evening,
}

interface TenantCalculatorFormModel {
  rent: number | null;
  rates: number | null;
  utilities: number | null;
  materials: number | null;
  leaseDuration: number | null;
  tradingHours: { [index: number]: TradingTime[] };
}

class TenantCalculatorPage extends Component<
  RouteComponentProps,
  TenantCalculatorState
> {
  state = {
    leaseTerm: null,
    formStatus: FormStatus.None,
    savings: null,
  };

  private readonly validation: ValidationGroup;
  private readonly model: TenantCalculatorFormModel;

  constructor(props: any) {
    super(props);
    this.model = {
      rent: null,
      rates: null,
      utilities: null,
      materials: null,
      leaseDuration: null,
      tradingHours: {},
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("rent", {
      required: Validators.required,
    });
    this.validation.addField("rates", {
      required: (val: any) =>
        this.state.leaseTerm !== LeaseTerm.LongTerm || Validators.required(val),
    });
    this.validation.addField("utilities", {
      required: (val: any) =>
        this.state.leaseTerm !== LeaseTerm.LongTerm || Validators.required(val),
    });
    this.validation.addField("materials", {
      required: (val: any) =>
        this.state.leaseTerm !== LeaseTerm.ShortTerm ||
        Validators.required(val),
    });
    this.validation.addField("leaseDuration", {
      required: (val: any) =>
        this.state.leaseTerm !== LeaseTerm.ShortTerm ||
        Validators.required(val),
    });
    this.validation.addField("tradingHours", {
      required: (val: any) =>
        !!Object.values(val).find((v) => (v as TradingTime[]).length > 0),
    });
    this.validation.addFieldListener(
      "tradingHours",
      this.onTradingHoursValidChanged
    );
  }

  componentWillUnmount(): void {
    this.validation.removeFieldListener(
      "tradingHours",
      this.onTradingHoursValidChanged
    );
  }

  onLeaseTermChange = (term: LeaseTerm) => {
    this.setState({ leaseTerm: term, savings: null });
  };

  onFieldInput = () => {
    this.setState({ formStatus: FormStatus.None, savings: null });
  };

  renderCurrencyField(
    name: string,
    label: string,
    requiredMessage: string,
    disabled: boolean
  ) {
    return (
      <div className="currency-field">
        <span className="symbol">£</span>
        <NumberField
          name={name}
          label={label}
          required={true}
          minValue={0}
          maxValue={100000}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{ required: requiredMessage }}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
      </div>
    );
  }

  renderLongTermsFields(disabled: boolean) {
    return (
      <section className="costs">
        <h3>How much are/were you paying in rent per month?</h3>
        {this.renderCurrencyField(
          "rent",
          "Cost of Rent (Per Month)",
          "Cost of rent is required",
          disabled
        )}
        <h3>How much are/were you paying in rates per month?</h3>
        {this.renderCurrencyField(
          "rates",
          "Cost of Rates (Per Month)",
          "Cost of rates is required",
          disabled
        )}
        <h3>How much are/were you paying in utilities per month?</h3>
        {this.renderCurrencyField(
          "utilities",
          "Cost of Utilities (Per Month)",
          "Cost of utilities is required",
          disabled
        )}
      </section>
    );
  }

  renderShortTermsFields(disabled: boolean) {
    const days = [];
    for (let k = 1; k <= config.tenancyCalculator.maxShortTermDays; k++) {
      days.push(k);
    }
    return (
      <section className="costs">
        <h3>How much are/were you paying in rent per month?</h3>
        {this.renderCurrencyField(
          "rent",
          "Cost of Rent (Per Day)",
          "Cost of rent is required",
          disabled
        )}
        <h3>How long did you lease the space for?</h3>
        <SelectField
          name="leaseDuration"
          label="Select time period"
          model={this.model}
          validationGroup={this.validation}
          validationMessages={{ required: "Lease period is required" }}
          disabled={disabled}
          onChange={this.onFieldInput}
        >
          {days.map((d) => (
            <MenuItem key={d} value={d}>
              {d > 1 ? `${d} days` : "1 day"}
            </MenuItem>
          ))}
        </SelectField>
        <h3>How much did fit out and materials costs?</h3>
        {this.renderCurrencyField(
          "materials",
          "Cost of Materials",
          "Cost of materials is required",
          disabled
        )}
      </section>
    );
  }

  onTradingHoursValidChanged = (errors: string[], forceDirty: boolean) => {
    this.setState({ formStatus: FormStatus.None, savings: null }); // HACK: force trading hours to rerender
  };

  onTradingTimeChanged = (
    ev: ChangeEvent<HTMLInputElement>,
    dayIndex: number,
    tradingTime: TradingTime
  ) => {
    const tradingHours: { [index: number]: TradingTime[] } =
      this.model.tradingHours;
    let dayTradingHours: TradingTime[] | undefined = tradingHours[dayIndex];
    const timeIndex = dayTradingHours
      ? dayTradingHours.indexOf(tradingTime)
      : -1;
    if (ev.target.checked) {
      if (timeIndex < 0) {
        if (!dayTradingHours) {
          dayTradingHours = [];
          tradingHours[dayIndex] = dayTradingHours;
        }
        dayTradingHours.push(tradingTime);
      }
    } else if (timeIndex >= 0) {
      dayTradingHours.splice(timeIndex, 1);
    }
    this.validation.validateField("tradingHours");
    this.setState({}); // HACK: force rerender
  };

  renderTradingHoursSection() {
    const tradingHours: { [index: number]: TradingTime[] } =
      this.model.tradingHours;
    const errors = this.validation.getFieldErrors("tradingHours");
    const displayedErrorID = errors ? errors[0] : null;
    const displayedError =
      displayedErrorID === "required"
        ? "At least one trading time is required"
        : displayedErrorID;
    return (
      <section className="hours">
        <h3>What are/were your top trading hours?</h3>
        <table>
          <thead>
            <tr>
              <th />
              <th>Morning</th>
              <th>Daytime</th>
              <th>Evening</th>
            </tr>
          </thead>
          <tbody>
            {DAY_NAMES.map((dayName: string, index: number) => {
              const dayTradingHours = tradingHours[index];
              return (
                <tr key={index}>
                  <td>{dayName}</td>
                  <td>
                    <Checkbox
                      checked={
                        !!(
                          dayTradingHours &&
                          dayTradingHours.indexOf(TradingTime.Morning) >= 0
                        )
                      }
                      onChange={(ev) =>
                        this.onTradingTimeChanged(
                          ev,
                          index,
                          TradingTime.Morning
                        )
                      }
                    />
                  </td>
                  <td>
                    <Checkbox
                      checked={
                        !!(
                          dayTradingHours &&
                          dayTradingHours.indexOf(TradingTime.Daytime) >= 0
                        )
                      }
                      onChange={(ev) =>
                        this.onTradingTimeChanged(
                          ev,
                          index,
                          TradingTime.Daytime
                        )
                      }
                    />
                  </td>
                  <td>
                    <Checkbox
                      checked={
                        !!(
                          dayTradingHours &&
                          dayTradingHours.indexOf(TradingTime.Evening) >= 0
                        )
                      }
                      onChange={(ev) =>
                        this.onTradingTimeChanged(
                          ev,
                          index,
                          TradingTime.Evening
                        )
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedError && <FormHelperText>{displayedError}</FormHelperText>}
      </section>
    );
  }

  onFormSubmit = () => {
    let currentCosts = 0;
    if (this.state.leaseTerm === LeaseTerm.LongTerm) {
      currentCosts +=
        12 * (this.model.rent! + this.model.rates! + this.model.utilities!);
    } else {
      currentCosts +=
        this.model.rent! * this.model.leaseDuration! + this.model.materials!;
    }
    let potentialCosts = 0;
    const rates = config.tenancyCalculator.rates;
    for (let k = 0; k < rates.length; k++) {
      const dayTimes = this.model.tradingHours[k];
      if (!dayTimes) {
        continue;
      }
      if (dayTimes.indexOf(TradingTime.Morning) >= 0) {
        potentialCosts += rates[k][0];
      }
      if (dayTimes.indexOf(TradingTime.Daytime) >= 0) {
        potentialCosts += rates[k][1];
      }
      if (dayTimes.indexOf(TradingTime.Evening) >= 0) {
        potentialCosts += rates[k][2];
      }
    }
    if (this.state.leaseTerm === LeaseTerm.LongTerm) {
      potentialCosts *= 52;
    } else {
      potentialCosts = Math.ceil(
        (potentialCosts * this.model.leaseDuration!) / 7
      );
    }
    const savings = Math.max(0, currentCosts - potentialCosts);
    this.setState({ savings: savings });
  };

  onContactClick = () => {
    window.location.href = NextRouteURLs.CONTACT
  };

  onBookClick = () => {
    this.props.history.push(NextRouteURLs.SPACES);
  };

  render() {
    const formDisabled =
      this.state.formStatus === FormStatus.Submitting ||
      this.state.formStatus === FormStatus.Success;
    return (
      <DefaultLayout id="tenant-calculator-page">
        <Form onSubmit={this.onFormSubmit} validationGroup={this.validation}>
          <section className="banner">
            <h1>Are you a business owner?</h1>
            <h2>How much could you save?</h2>
          </section>
          <section className="intro">
            <h2>Tell us about your retail experiences?</h2>
            <p>
              Using Sook's "Space as a Service" approach could save your
              business money.  
            </p>
            <p>
              With Sook you only pay for your business to be in the communities
              you want it to be, at the times it needs to be.
            </p>
            <p>
                Choose what lease you currently have (or are considering) to see
              how much you could save with Sook.
            </p>
            <h3>Select your current retail lease</h3>
            <div className="lease-term">
              <ToggleButton
                selected={this.state.leaseTerm === LeaseTerm.LongTerm}
                onChange={() => this.onLeaseTermChange(LeaseTerm.LongTerm)}
              >
                Long term lease
              </ToggleButton>
              <ToggleButton
                selected={this.state.leaseTerm === LeaseTerm.ShortTerm}
                onChange={() => this.onLeaseTermChange(LeaseTerm.ShortTerm)}
              >
                Short term lease (Pop-up)
              </ToggleButton>
            </div>
          </section>
          {this.state.leaseTerm === LeaseTerm.LongTerm &&
            this.renderLongTermsFields(formDisabled)}
          {this.state.leaseTerm === LeaseTerm.ShortTerm &&
            this.renderShortTermsFields(formDisabled)}
          {this.state.leaseTerm !== null && this.renderTradingHoursSection()}
          {this.state.leaseTerm !== null && (
            <section className="actions">
              <Button type="submit">Calculate</Button>
            </section>
          )}
          {this.state.savings !== null && (
            <section className="result">
              <p>By switching to Sook, you could save</p>
              <p className="savings">
                {formatCurrency(this.state.savings!)} per year
              </p>
              <p>
                Plus gain access to many more benefits from having a Sook,
                including the ability to instantly change the look and feel, and
                the use of our analytics tool, to improve your results.
              </p>
              <div className="actions">
                <Button onClick={this.onContactClick}>Get in touch</Button>
                <Button onClick={this.onBookClick}>Book now</Button>
              </div>
            </section>
          )}
        </Form>
      </DefaultLayout>
    );
  }
}

export default withRouter(TenantCalculatorPage);
